/**
 * URL Normalizer Utility
 * 
 * Normalizes media URLs to ensure consistent /api/uploads/** paths.
 * Used when loading data from localStorage/IndexedDB that might contain
 * legacy absolute URLs or deprecated path formats.
 * 
 * URL CONTRACT:
 * - Background presets: /api/uploads/backgrounds/presets/<filename>
 * - Custom backgrounds: /api/uploads/backgrounds/custom/<filename>
 * - Widget images: /api/uploads/widgets/<filename>
 * - Widget videos: /api/uploads/videos/<filename>
 */

/**
 * Normalize any media URL to a relative /api/uploads/... path.
 * 
 * Handles:
 * - Absolute URLs with any origin (strips to relative path)
 * - Legacy bare preset filenames ("forest-01.jpg")
 * - Root-level paths ("/forest-01.jpg")
 * - Already-correct relative paths (returns unchanged)
 * 
 * @param {string} url - URL to normalize
 * @returns {string} Normalized relative URL or original if can't normalize
 */
export function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  
  // Already a proper relative /api/uploads/ URL
  if (url.startsWith('/api/uploads/')) {
    return url;
  }

  // Phase 5b: Don't normalize S3 presigned URLs (cloud-stored assets).
  // These are generated at runtime by useAssetHydration and should be left as-is.
  // They typically contain S3 endpoint domains and query parameters like X-Amz-Signature.
  if (url.includes('X-Amz-Signature') || url.includes('x-amz-signature')) {
    return url;
  }
  
  // Strip origin from absolute URLs containing /api/uploads/
  // e.g., "https://old-domain.com/api/uploads/widgets/image.jpg" -> "/api/uploads/widgets/image.jpg"
  const apiUploadsMatch = url.match(/\/api\/uploads\/(.+)$/);
  if (apiUploadsMatch) {
    return `/api/uploads/${apiUploadsMatch[1]}`;
  }
  
  // Handle legacy bare preset filenames like "forest-01.jpg", "forest-02.jpg"
  if (/^forest-\d+\.jpg$/i.test(url)) {
    return `/api/uploads/backgrounds/presets/${url.toLowerCase()}`;
  }
  
  // Handle legacy root-level preset paths like "/forest-01.jpg"
  const rootPresetMatch = url.match(/^\/(forest-\d+\.jpg)$/i);
  if (rootPresetMatch) {
    return `/api/uploads/backgrounds/presets/${rootPresetMatch[1].toLowerCase()}`;
  }
  
  // Return original if we can't normalize (might be valid external URL or data URL)
  return url;
}

/**
 * Normalize all media URLs in a slide data object.
 * 
 * @param {Object} slide - Slide data object
 * @returns {Object} Slide with normalized URLs
 */
export function normalizeSlideUrls(slide) {
  if (!slide) return slide;
  
  const normalized = { ...slide };
  
  // Normalize backgroundUrl
  if (normalized.backgroundUrl) {
    normalized.backgroundUrl = normalizeMediaUrl(normalized.backgroundUrl);
  }
  
  // Normalize imageWidget.src
  if (normalized.imageWidget?.src) {
    normalized.imageWidget = {
      ...normalized.imageWidget,
      src: normalizeMediaUrl(normalized.imageWidget.src),
    };
  }
  
  // Normalize videoWidget.src
  if (normalized.videoWidget?.src) {
    normalized.videoWidget = {
      ...normalized.videoWidget,
      src: normalizeMediaUrl(normalized.videoWidget.src),
    };
  }
  
  return normalized;
}

/**
 * Normalize all media URLs in an array of slides.
 * 
 * @param {Array} slides - Array of slide data objects
 * @returns {Array} Slides with normalized URLs
 */
export function normalizeCarouselUrls(slides) {
  if (!Array.isArray(slides)) return slides;
  return slides.map(normalizeSlideUrls);
}
